import { Components } from '@mui/material/styles';
import { CSSObject, Theme } from '@mui/material';
import tokens from '@skodaflow/web-tokens/src/tokens.json';
import { TypographySize } from '../typography/typography.theme';

export const getTypographyLineHeight = (element: string, size: TypographySize): CSSObject => {
  const typo = tokens['flow-typography'].find((el) => el.name === `${size}-${element}`);
  if (typo) {
    return {
      height: typo['style'].lineHeight,
    };
  } else {
    return {};
  }
};

export const getResponsiveLineHeight = (element: string, theme: Theme): CSSObject => {
  return {
    ...getTypographyLineHeight(element, 'small'),
    [theme.breakpoints.up('md')]: {
      ...getTypographyLineHeight(element, 'medium'),
    },
    [theme.breakpoints.up('xl')]: {
      ...getTypographyLineHeight(element, 'large'),
    },
  };
};

export const getTextVariantStyle = (): CSSObject => ({
  borderRadius: '4px',
  // style taken from @mui, lowering size
  transform: 'scale(1, 0.60)',
  transformOrigin: '0 55%',
});

export const flowSkeleton: Components<Theme>['MuiSkeleton'] = {
  defaultProps: {
    animation: 'wave',
    ['aria-label']: 'placeholder',
    role: 'progressbar',
  },
  styleOverrides: {
    text: {
      ...getTextVariantStyle(),
    },
    circular: {
      flexShrink: '0',
    },
    wave: ({ theme }) => ({
      backgroundColor: theme.palette.onSurface.os100,
      '&:after': {
        background: `
          linear-gradient(
            90deg,
            transparent,
            ${theme.palette.surface.secondary},
            transparent
          )
        `,
      },
      ...(theme.palette.mode === 'dark' && {
        backgroundColor: theme.palette.surface.secondary,
        '&:after': {
          background: `
             linear-gradient(
               90deg,
               transparent,
               ${theme.palette.surface.primary},
               transparent
             )
           `,
        },
      }),
    }),
  },
  variants: [
    {
      props: { variant: 'display' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('display', theme),
        ...getTextVariantStyle(),
        width: '600px',
      }),
    },
    {
      props: { variant: 'h1' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('h1', theme),
        ...getTextVariantStyle(),
        width: '540px',
      }),
    },
    {
      props: { variant: 'h2' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('h2', theme),
        ...getTextVariantStyle(),
        width: '510px',
      }),
    },
    {
      props: { variant: 'h3' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('h3', theme),
        ...getTextVariantStyle(),
        width: '480px',
      }),
    },
    {
      props: { variant: 'h4' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('h4', theme),
        ...getTextVariantStyle(),
        width: '450px',
      }),
    },
    {
      props: { variant: 'subheadline' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('subheadline', theme),
        ...getTextVariantStyle(),
        width: '420px',
      }),
    },
    {
      props: { variant: 'body' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('body', theme),
        ...getTextVariantStyle(),
        width: '480px',
      }),
    },
    {
      props: { variant: 'disclaimer' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('disclaimer', theme),
        ...getTextVariantStyle(),
        width: '360px',
      }),
    },
    {
      props: { variant: 'disclaimer1' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('disclaimer1', theme),
        ...getTextVariantStyle(),
        width: '360px',
      }),
    },
    {
      props: { variant: 'disclaimer2' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('disclaimer2', theme),
        ...getTextVariantStyle(),
        width: '360px',
      }),
    },
    {
      props: { variant: 'caption1' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('caption1', theme),
        ...getTextVariantStyle(),
        width: '300px',
      }),
    },
    {
      props: { variant: 'caption2' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('caption2', theme),
        ...getTextVariantStyle(),
        width: '300px',
      }),
    },
    {
      props: { variant: 'list' },
      style: ({ theme }) => ({
        ...getResponsiveLineHeight('list', theme),
        ...getTextVariantStyle(),
        width: '600px',
      }),
    },
  ],
};
